// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * One maintenance record in the timeline's floating card: what the record's page shows —
 * when, which versions, type, description, Jira, who, and each line — laid out for a
 * narrow card.
 *
 * Named as the timeline's archive names it: the record's number in that archive's change
 * log and the versions it took that archive between. For a record inside a fitted module
 * those are the robot's, not the module's own #n and versions, which would not match
 * anything else on the page.
 *
 * Read-only on purpose. Editing or revoking a record (§8.6.3) stays on its page, one click
 * away through "open the full page".
 */

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api } from "@/api/endpoints";
import { ChangeSummary } from "./ChangeType";
import { ErrorBanner } from "./ErrorBanner";
import { JiraLink } from "./JiraLink";
import { EventDetail } from "./MaintenanceEventDetail";

export function MaintenanceRecordPanel({
  archiveId,
  eventId,
  recordNo,
  fromVersion,
  toVersion,
}: {
  /** The archive the record is on — a fitted module's, for a `MODULE` version. */
  archiveId: string;
  eventId: string;
  /** Its number in the timeline archive's change log. */
  recordNo: number | null | undefined;
  /** The timeline archive's versions before and after it. */
  fromVersion: number | null;
  toVersion: number;
}) {
  const { t } = useTranslation();
  const event = useQuery({
    queryKey: ["maintenance-event", archiveId, eventId],
    queryFn: () => api.maintenance.get(archiveId, eventId),
    // An event never changes once written (§7.6).
    staleTime: Number.POSITIVE_INFINITY,
  });

  return (
    <div className="timeline-record">
      <ErrorBanner error={event.error} />
      {event.isLoading && <p className="muted">{t("common.loading")}</p>}
      {event.data && (
        <>
          <p className="timeline-record-title">
            {t("maintenance.eventTitle", { n: recordNo ?? event.data.sequence_no })}
            <span className="muted">
              {" · "}
              {fromVersion ? `v${fromVersion} → ` : ""}v{toVersion}
            </span>
          </p>
          <p>
            <ChangeSummary changeType={event.data.change_type} reason={event.data.reason} />{" "}
            <JiraLink url={event.data.jira_url} />
          </p>
          <p className="muted">
            {t("maintenance.occurredAt")}: {new Date(event.data.occurred_at).toLocaleString()}
          </p>
          <EventDetail compact archiveId={archiveId} event={event.data} />
          <Link to={`/archives/${archiveId}/maintenance/${eventId}`}>
            {t("archive.openFullRecord")}
          </Link>
        </>
      )}
    </div>
  );
}
